<template>
  <div class="container mt-2">
    <div class="d-flex flex-row align-items-center gap-2 mb-2">
      <input 
        type="text" 
        class="form-control m-0 sombra-sm" 
        :placeholder="$t('buscarPorNombre')+'...'" 
        v-model="searchTerm" 
        @input="onSearchInput"
      />
      <router-link to="/personas/crearPermiso" class="btn bg-goethe-accent p-1 rounded sombra-sm">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 12H18M12 6V18" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </router-link>
    </div>

    <table class="table-striped w-100 mb-2 sombra-sm">
      <thead>
        <tr>
          <th @click="sortTable('permiso')" style="cursor: pointer;" class="user-select-none">
            {{ $t('permiso') }}
            <span v-if="sortKey === 'permiso'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortTable('Descripcion')" style="cursor: pointer;" class="user-select-none">
            {{ $t('descripcion') }}
            <span v-if="sortKey === 'Descripcion'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortTable('Categoria')" style="cursor: pointer;" class="user-select-none">
            {{ $t('categoria') }}
            <span v-if="sortKey === 'Categoria'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortTable('SubCategoria')" style="cursor: pointer;" class="user-select-none">
            {{ $t('subCategoria') }}
            <span v-if="sortKey === 'SubCategoria'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="permiso in permisos" :key="permiso.IdPermiso">
          <tr @click="toggleDetalle(permiso.IdPermiso)" style="cursor: pointer;">
            <td class="fs-8 align-middle p-2">
              <p class="m-0 font-weight-bold">{{ permiso.permiso }}</p>
            </td>
            <td class="fs-8 align-middle p-2">
              <p class="m-0">{{ permiso.Descripcion || '-' }}</p>
            </td>
            <td class="fs-8 align-middle p-2">
              <span v-if="permiso.Categoria" class="badge fondo-gris">{{ permiso.Categoria }}</span>
            </td>
            <td class="fs-8 align-middle p-2">
              <p class="m-0 texto-secundario">{{ permiso.SubCategoria || '-' }}</p>
            </td>
          </tr>
        </template>
        <tr v-if="permisos.length === 0 && !loading">
          <td colspan="4" class="text-center p-3 texto-secundario">{{ $t('sinResultados') || 'No se encontraron permisos.' }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="loading-spinner d-flex flex-row align-items-center justify-content-center">
      <img src="@/assets/LOADING.gif" style="width: 80px;" alt="Cargando..." />
    </div>

    <nav aria-label="Page navigation" class="d-flex flex-row justify-content-between" v-if="totalPages > 1">
      <ul class="pagination">
        <li class="p-2 rounded-start bg-goethe-accent-claro-3 borde-secundario border-1 border-end sombra-sm" :class="{ disabled: currentPage === 1 }">
          <a class="p-2 text-dark text-decoration-none" href="#" @click.prevent="changePage(currentPage - 1)">{{ $t('anterior') }}</a>
        </li>
        <li class="p-2 bg-goethe-accent-claro-3 borde-secundario sombra-sm">
          <a class="p-2 text-dark text-decoration-none" href="#" @click.prevent="">{{ currentPage }} {{$t('de')}} {{ totalPages }}</a>
        </li>
        <li class="p-2 rounded-end bg-goethe-accent-claro-3 borde-secundario border-1 border-start sombra-sm" :class="{ disabled: currentPage === totalPages }">
          <a class="p-2 text-dark text-decoration-none" href="#" @click.prevent="changePage(currentPage + 1)">{{ $t('siguiente') }}</a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import api from '@/services/api';

export default {
  name: 'VerPermisos',
  setup() {
    const { t } = useI18n();
    const permisos = ref([]);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const itemsPerPage = 15;
    const totalPages = ref(1);
    const loading = ref(false);
    const hasPermisosPermission = ref(false);
    
    const router = useRouter();
    const store = useStore();
    const user = computed(() => store.state.user);
    
    const sortKey = ref('');
    const sortDirection = ref('asc');
    let timeoutBusqueda = null;

    const checkAuth = async () => {
      if (user.value && user.value.personasPermisos) {
        // Usamos la política que protege tus endpoints de creación/edición en backend
        const permisoAComprobar = 'AsignarPermisos';
        
        const tienePermisoDirecto = user.value.personasPermisos.some(p => p.idPermisoNavigation?.permiso === permisoAComprobar);
        const tienePermisoPorRol = user.value.personasRoles?.some(r => r.idRolNavigation?.permisos?.some(p => p.permiso === permisoAComprobar));

        if (tienePermisoDirecto || tienePermisoPorRol) {
          console.log("Tiene Permiso para gestionar permisos");
          hasPermisosPermission.value = true;
        } else {
          console.log('Usuario no tiene permiso a acceder.');
          await router.push('/');
        }
      }
    };

    // Consumo del nuevo Endpoint Server-Side Paginado
    const fetchPermisos = async () => {
      try {
        loading.value = true;
        
        // Invocamos la ruta con los query params correspondientes
        const response = await api.get(`api/permisos/search`, {
          params: {
            term: searchTerm.value,
            page: currentPage.value,
            pageSize: itemsPerPage
          }
        });

        // Mapeamos los datos según el PaginatedResult<T> de tu C#
        permisos.value = response.data.Items || [];
        totalPages.value = response.data.TotalPages || 1;

        console.log(permisos.value)
        
        // Ordenamiento local rápido por si cambian de columna
        sortLocalData();
        
        loading.value = false;
      } catch (error) {
        console.error('Error al obtener permisos:', error);
        loading.value = false;
      }
    };

    // Debounce para evitar saturar el servidor en cada tecla presionada
    const onSearchInput = () => {
      clearTimeout(timeoutBusqueda);
      timeoutBusqueda = setTimeout(() => {
        currentPage.value = 1; // Volver a la primera página con nuevo filtro
        fetchPermisos();
      }, 350); 
    };

    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
      fetchPermisos();
    };

    const toggleDetalle = async (permisoId) => {
      // Redirección a la edición del permiso específico
      await router.push(`/personas/editarPermiso?id=${permisoId}`);
    };

    const sortLocalData = () => {
      if (!sortKey.value) return;
      
      permisos.value.sort((a, b) => {
        const valA = (a[sortKey.value] || '').toString().toLowerCase();
        const valB = (b[sortKey.value] || '').toString().toLowerCase();

        if (sortDirection.value === 'asc') {
          return valA > valB ? 1 : valA < valB ? -1 : 0;
        } else {
          return valA < valB ? 1 : valA > valB ? -1 : 0;
        }
      });
    };

    const sortTable = (key) => {
      if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortDirection.value = 'asc';
      }
      sortLocalData();
    };

    onMounted(() => {
      const lastUpdate = localStorage.getItem("lastUpdate_permisos");
      const today = new Date().toISOString().split("T")[0];

      if (lastUpdate !== today) {
        localStorage.setItem("lastUpdate_permisos", today);
        window.location.reload();
      }
      checkAuth();
      fetchPermisos();
    });

    return {
      searchTerm,
      onSearchInput,
      changePage,
      currentPage,
      totalPages,
      sortTable,
      sortKey,
      sortDirection,
      toggleDetalle,
      loading,
      t,
      hasPermisosPermission,
      permisos,
    };
  },
};
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600;
}
</style>